import React, { useEffect, useMemo, useState } from "react";
import {
  Paperclip,
  X,
  FileText,
  Image as ImageIcon,
  Plus,
  ExternalLink,
} from "lucide-react";
import YmdDateInput from "../ui/YmdDateInput";
import CustomSelect from "../ui/CustomSelect";
import Toast from "../ui/Toast";
import {
  createDisconnectRequest,
  listProviderCircuits,
  DISCONNECT_REASONS,
  DISCONNECT_STATUS,
  statusLabel,
  statusStepIndex,
} from "../../services/disconnectService";
import { formatConcatenatedAddress } from "../../utils/helpers";

/** Hint for the file picker; upload itself accepts any extension. */
const ACCEPTED_EXTENSIONS = "*/*";

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function todayYmd() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function circuitFromInventoryItem(item) {
  if (!item) return null;
  return {
    inventory_id: item.id ?? item.inventory_id ?? null,
    circuit_id: item.circuit_id || "",
    address:
      item.address ||
      (formatConcatenatedAddress(item) !== "-"
        ? formatConcatenatedAddress(item)
        : ""),
    service_type: item.service_type || "",
    billing_account: item.billing_account || "",
    mrc: item.mrc != null ? Number(item.mrc) : null,
  };
}

let tempGroupSeq = 1;
function nextTempGroupId() {
  return `new-${tempGroupSeq++}`;
}

function emptyGroup(seedCircuit = null) {
  const key = nextTempGroupId();
  return {
    key,
    date_submitted_to_supplier: todayYmd(),
    tracking_number: "",
    supplier_contact: "",
    supplier_confirmed_disconnect_date: "",
    last_expected_bill_date: "",
    circuits: seedCircuit ? [seedCircuit] : [],
  };
}

function DisconnectStepper({ status }) {
  const step = statusStepIndex(status);
  const steps = [
    { id: 1, label: "Submitted" },
    { id: 2, label: "Confirmed by supplier" },
    { id: 3, label: "Billing stopped" },
  ];
  return (
    <div className="flex items-center gap-2 text-[13px] flex-wrap">
      {steps.map((s, idx) => {
        const active = step === s.id;
        const done = step > s.id;
        return (
          <React.Fragment key={s.id}>
            {idx > 0 && (
              <div className="w-8 h-px bg-[var(--line,#e4e2dc)] hidden sm:block" />
            )}
            <div className="flex items-center gap-2">
              <span
                className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold ${
                  done
                    ? "bg-[var(--good,#1f6b34)] text-white"
                    : active
                      ? "bg-[var(--accent,#a51c1c)] text-white"
                      : "bg-[var(--sunken,#eeece7)] text-[var(--muted,#6b6a66)]"
                }`}
              >
                {done ? "✓" : s.id}
              </span>
              <span
                className={
                  active
                    ? "font-semibold text-[var(--accent,#a51c1c)]"
                    : done
                      ? "text-[var(--good,#1f6b34)]"
                      : "text-[var(--muted,#6b6a66)]"
                }
              >
                {s.label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function StatusPill({ status }) {
  let cls = "bg-sky-100 text-sky-900 border-sky-200";
  if (status === DISCONNECT_STATUS.CONFIRMED) {
    cls = "bg-amber-100 text-amber-900 border-amber-200";
  } else if (status === DISCONNECT_STATUS.BILLING_STOPPED) {
    cls = "bg-emerald-100 text-emerald-900 border-emerald-200";
  }
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${cls}`}
    >
      {statusLabel(status)}
    </span>
  );
}

function fileKindLabel(file) {
  const name = (file?.name || "").toLowerCase();
  if (/\.(png|jpe?g|gif|webp|bmp|tiff?)$/.test(name)) return "Image";
  if (/\.pdf$/.test(name)) return "PDF";
  if (/\.(eml|msg)$/.test(name)) return "Email";
  if (/\.(docx?|rtf)$/.test(name)) return "Word";
  if (/\.txt$/.test(name)) return "Text";
  return "File";
}

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * New disconnect request modal (from Edit Inventory → Disconnect).
 * One provider; circuits only for that provider.
 * Supplier order groups can be created here (different tracking numbers)
 * or after submit on the view/edit screen.
 */
const DisconnectRequestModal = ({ item, tenantId, onClose, onSubmitted }) => {
  const provider = item?.provider || "";
  const seed = useMemo(() => circuitFromInventoryItem(item), [item]);

  const [groups, setGroups] = useState(() => [emptyGroup(seed)]);
  const [requestedBy, setRequestedBy] = useState("Hilton Sturisky");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({
    title: "",
    message: null,
    type: "error",
  });
  const showErrorToast = (message) => {
    setToast({
      title: "Cannot submit",
      message: message || "Something went wrong",
      type: "error",
    });
  };
  const [showAddCircuits, setShowAddCircuits] = useState(false);
  const [addToGroupKey, setAddToGroupKey] = useState(null);
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerRows, setPickerRows] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [selectedPickerIds, setSelectedPickerIds] = useState(() => new Set());

  const allCircuits = useMemo(
    () => groups.flatMap((g) => g.circuits || []),
    [groups]
  );
  const totalMrc = allCircuits.reduce((sum, c) => sum + (Number(c.mrc) || 0), 0);

  const previewStatus = useMemo(() => {
    const hasTracking = groups.some((g) =>
      String(g.tracking_number || "").trim()
    );
    return hasTracking
      ? DISCONNECT_STATUS.CONFIRMED
      : DISCONNECT_STATUS.SUBMITTED;
  }, [groups]);

  const fieldClass =
    "w-full rounded-md border border-[var(--line,#e4e2dc)] bg-[var(--sunken,#eeece7)] px-3 py-2 text-[13px] text-[var(--ink,#1c1c1a)] placeholder:text-[var(--muted,#6b6a66)] focus:outline-none focus:ring-1 focus:ring-[var(--accent,#a51c1c)]";

  const updateGroup = (key, patch) => {
    setGroups((prev) =>
      prev.map((g) => (g.key === key ? { ...g, ...patch } : g))
    );
  };

  const addSupplierOrderGroup = () => {
    setGroups((prev) => [...prev, emptyGroup(null)]);
  };

  const removeGroup = (key) => {
    setGroups((prev) => {
      if (prev.length <= 1) return prev;
      const target = prev.find((g) => g.key === key);
      const remaining = prev.filter((g) => g.key !== key);
      if (!target || (target.circuits || []).length === 0) return remaining;
      // Move leftover circuits into the first remaining group
      return remaining.map((g, i) =>
        i === 0
          ? { ...g, circuits: [...(g.circuits || []), ...(target.circuits || [])] }
          : g
      );
    });
  };

  const moveCircuitToGroup = (circuitId, fromKey, toKey) => {
    if (!toKey || fromKey === toKey) return;
    setGroups((prev) => {
      let moved = null;
      const without = prev.map((g) => {
        if (g.key !== fromKey) return g;
        const nextCircuits = (g.circuits || []).filter((c) => {
          if (c.circuit_id === circuitId) {
            moved = c;
            return false;
          }
          return true;
        });
        return { ...g, circuits: nextCircuits };
      });
      if (!moved) return prev;
      return without.map((g) =>
        g.key === toKey
          ? { ...g, circuits: [...(g.circuits || []), moved] }
          : g
      );
    });
  };

  const removeCircuit = (groupKey, circuitId) => {
    setGroups((prev) => {
      const next = prev.map((g) =>
        g.key === groupKey
          ? {
              ...g,
              circuits: (g.circuits || []).filter(
                (c) => c.circuit_id !== circuitId
              ),
            }
          : g
      );
      const total = next.reduce((n, g) => n + (g.circuits || []).length, 0);
      // Keep at least one circuit on the request
      if (total === 0) return prev;
      return next;
    });
  };

  const openAddCircuits = (groupKey) => {
    setAddToGroupKey(groupKey);
    setSelectedPickerIds(new Set());
    setPickerSearch("");
    setShowAddCircuits(true);
  };

  useEffect(() => {
    if (!showAddCircuits || !provider || !tenantId) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      setPickerLoading(true);
      try {
        const res = await listProviderCircuits(tenantId, provider, {
          search: pickerSearch,
        });
        if (cancelled) return;
        const existing = new Set(
          allCircuits.map((c) => String(c.circuit_id || "").toLowerCase())
        );
        setPickerRows(
          (res?.data || []).filter(
            (r) => !existing.has(String(r.circuit_id || "").toLowerCase())
          )
        );
      } catch {
        if (!cancelled) setPickerRows([]);
      } finally {
        if (!cancelled) setPickerLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [showAddCircuits, provider, tenantId, pickerSearch, allCircuits]);

  const addSelectedCircuits = () => {
    const toAdd = pickerRows
      .filter((r) => selectedPickerIds.has(r.inventory_id))
      .map((r) => ({
        inventory_id: r.inventory_id,
        circuit_id: r.circuit_id,
        address: r.address || "",
        service_type: r.service_type || "",
        billing_account: r.billing_account || "",
        mrc: r.mrc != null ? Number(r.mrc) : null,
      }));
    setGroups((prev) =>
      prev.map((g) =>
        g.key === addToGroupKey
          ? { ...g, circuits: [...(g.circuits || []), ...toAdd] }
          : g
      )
    );
    setSelectedPickerIds(new Set());
    setShowAddCircuits(false);
    setPickerSearch("");
  };

  const onFilesPicked = (fileList) => {
    const next = Array.from(fileList || []);
    if (next.length === 0) return;
    setFiles((prev) => [...prev, ...next]);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!provider) {
      showErrorToast("Provider is required.");
      return;
    }
    if (allCircuits.length === 0) {
      showErrorToast("Add at least one circuit.");
      return;
    }

    // Drop empty groups that have no circuits (except keep structure if all empty — already blocked)
    const groupsWithCircuits = groups.filter(
      (g) => (g.circuits || []).length > 0
    );
    if (groupsWithCircuits.length === 0) {
      showErrorToast("Add at least one circuit.");
      return;
    }

    // Tracking number requires Last Expected Bill Date (needed for billing stopped)
    const missingLeb = groupsWithCircuits.find(
      (g) =>
        String(g.tracking_number || "").trim() &&
        !String(g.last_expected_bill_date || "").trim()
    );
    if (missingLeb) {
      const idx = groups.findIndex((g) => g.key === missingLeb.key) + 1;
      showErrorToast(
        `Group ${idx}: Last expected bill date is required when a tracking number is set.`
      );
      return;
    }

    setSubmitting(true);
    try {
      const groupsPayload = groupsWithCircuits.map((g) => ({
        date_submitted_to_supplier: g.date_submitted_to_supplier || null,
        tracking_number: g.tracking_number || null,
        supplier_contact: g.supplier_contact || null,
        supplier_confirmed_disconnect_date:
          g.supplier_confirmed_disconnect_date || null,
        last_expected_bill_date: g.last_expected_bill_date || null,
        circuits: (g.circuits || []).map((c) => ({
          inventory_id: c.inventory_id,
          circuit_id: c.circuit_id,
          address: c.address,
          service_type: c.service_type,
          billing_account: c.billing_account,
          mrc: c.mrc,
        })),
      }));

      const result = await createDisconnectRequest(
        {
          tenant_id: tenantId,
          provider,
          requested_by: requestedBy || "Hilton Sturisky",
          reason: reason || null,
          notes: notes || null,
          groups: groupsPayload,
        },
        files
      );

      if (onSubmitted) onSubmitted(result?.data);
      onClose();
    } catch (err) {
      showErrorToast(err.message || "Failed to submit disconnect request");
    } finally {
      setSubmitting(false);
    }
  };

  if (!item) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-4">
      <Toast
        title={toast.title}
        message={toast.message}
        type={toast.type}
        duration={5000}
        onClose={() =>
          setToast({
            title: "",
            message: null,
            type: "error",
          })
        }
      />
      <div
        className="relative flex max-h-[92vh] w-full max-w-[960px] flex-col overflow-hidden rounded-[12px] border border-[var(--line,#e4e2dc)] bg-white shadow-[0_20px_60px_rgba(28,28,26,0.28)]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="disconnect-modal-title"
      >
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-shrink-0 border-b border-[var(--line,#e4e2dc)] px-6 pt-5 pb-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--accent,#a51c1c)]">
                  New disconnect request
                </p>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <h2
                    id="disconnect-modal-title"
                    className="text-[22px] font-semibold text-[var(--ink,#1c1c1a)]"
                  >
                    New disconnect
                  </h2>
                  {provider ? (
                    <span className="inline-flex items-center rounded-full border border-[var(--line,#e4e2dc)] bg-[var(--sunken,#eeece7)] px-2.5 py-0.5 text-[12px] font-medium text-[var(--ink,#1c1c1a)]">
                      {provider}
                    </span>
                  ) : null}
                </div>
                <p className="mt-1 text-[13px] text-[var(--muted,#6b6a66)]">
                  {allCircuits.length} circuit
                  {allCircuits.length === 1 ? "" : "s"} · {formatMoney(totalMrc)}{" "}
                  MRC being removed
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-md p-1.5 text-[var(--muted,#6b6a66)] hover:bg-[var(--sunken,#eeece7)]"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-4">
              <DisconnectStepper status={previewStatus} />
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5 space-y-6">
            {/* Supplier order groups */}
            <section>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="text-[14px] font-semibold text-[var(--ink,#1c1c1a)]">
                    Supplier order groups
                  </h3>
                  <p className="text-[12px] text-[var(--muted,#6b6a66)]">
                    Same tracking number → one group. Different tracking numbers
                    → add another group and move circuits into it. You can also
                    submit first and add groups later. Only{" "}
                    {provider || "this provider"} circuits can be added.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={addSupplierOrderGroup}
                  className="inline-flex items-center gap-1 rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white px-2.5 py-1.5 text-[12px] font-medium hover:bg-[var(--sunken,#eeece7)]"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add supplier order group
                </button>
              </div>

              <div className="space-y-4">
                {groups.map((g, gi) => (
                  <div
                    key={g.key}
                    className="overflow-hidden rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--line,#e4e2dc)] bg-[var(--sunken,#eeece7)] px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="text-[13px] font-semibold">
                          Group {gi + 1}
                        </span>
                        <StatusPill
                          status={
                            String(g.tracking_number || "").trim()
                              ? DISCONNECT_STATUS.CONFIRMED
                              : DISCONNECT_STATUS.SUBMITTED
                          }
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => openAddCircuits(g.key)}
                          className="inline-flex items-center gap-1 rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white px-2 py-1 text-[12px] font-medium"
                        >
                          <Plus className="h-3.5 w-3.5" />
                          Add circuits
                        </button>
                        {groups.length > 1 ? (
                          <button
                            type="button"
                            onClick={() => removeGroup(g.key)}
                            className="rounded p-1 text-[var(--muted,#6b6a66)] hover:bg-white"
                            aria-label={`Remove group ${gi + 1}`}
                            title="Remove group (circuits move to Group 1)"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        ) : null}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-3">
                      <label className="block">
                        <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
                          Date submitted to supplier
                        </span>
                        <YmdDateInput
                          value={g.date_submitted_to_supplier}
                          onChange={(e) =>
                            updateGroup(g.key, {
                              date_submitted_to_supplier: e.target.value,
                            })
                          }
                          className={fieldClass}
                        />
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
                          Supplier tracking or order number
                        </span>
                        <input
                          type="text"
                          value={g.tracking_number}
                          onChange={(e) =>
                            updateGroup(g.key, {
                              tracking_number: e.target.value,
                            })
                          }
                          placeholder="Order or ticket number"
                          className={fieldClass}
                        />
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
                          Supplier contact
                        </span>
                        <input
                          type="text"
                          value={g.supplier_contact}
                          onChange={(e) =>
                            updateGroup(g.key, {
                              supplier_contact: e.target.value,
                            })
                          }
                          placeholder="Name or email"
                          className={fieldClass}
                        />
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
                          Supplier confirmed disconnect date
                        </span>
                        <YmdDateInput
                          value={g.supplier_confirmed_disconnect_date}
                          onChange={(e) =>
                            updateGroup(g.key, {
                              supplier_confirmed_disconnect_date: e.target.value,
                            })
                          }
                          className={fieldClass}
                        />
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
                          Last expected bill date
                          {String(g.tracking_number || "").trim() ? (
                            <span className="text-[var(--crit,#c0262b)]"> *</span>
                          ) : null}
                        </span>
                        <YmdDateInput
                          value={g.last_expected_bill_date}
                          onChange={(e) =>
                            updateGroup(g.key, {
                              last_expected_bill_date: e.target.value,
                            })
                          }
                          className={fieldClass}
                          aria-label={
                            String(g.tracking_number || "").trim()
                              ? "Last expected bill date (required with tracking number)"
                              : "Last expected bill date"
                          }
                        />
                      </label>
                    </div>

                    <div className="overflow-x-auto border-t border-[var(--line,#e4e2dc)]">
                      <table className="w-full text-left text-[13px]">
                        <thead className="text-[11px] font-semibold uppercase text-[var(--muted,#6b6a66)]">
                          <tr>
                            <th className="px-3 py-2">Circuit ID</th>
                            <th className="px-3 py-2">Address</th>
                            <th className="px-3 py-2">Service type</th>
                            <th className="px-3 py-2">Billing account</th>
                            <th className="px-3 py-2 text-right">MRC</th>
                            {groups.length > 1 ? (
                              <th className="px-3 py-2">Move to group</th>
                            ) : null}
                            <th className="px-2 py-2 w-8" />
                          </tr>
                        </thead>
                        <tbody>
                          {(g.circuits || []).length === 0 ? (
                            <tr>
                              <td
                                colSpan={7}
                                className="px-3 py-3 text-[12px] text-[var(--muted,#6b6a66)]"
                              >
                                No circuits in this group yet. Add circuits or
                                move some from another group.
                              </td>
                            </tr>
                          ) : (
                            (g.circuits || []).map((c) => (
                              <tr
                                key={`${g.key}-${c.circuit_id}`}
                                className="border-t border-[var(--line,#e4e2dc)]"
                              >
                                <td className="px-3 py-2 font-medium">
                                  {c.circuit_id}
                                </td>
                                <td className="px-3 py-2 text-[var(--muted,#6b6a66)]">
                                  {c.address || "—"}
                                </td>
                                <td className="px-3 py-2">
                                  {c.service_type || "—"}
                                </td>
                                <td className="px-3 py-2">
                                  {c.billing_account || "—"}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums">
                                  {formatMoney(c.mrc)}
                                </td>
                                {groups.length > 1 ? (
                                  <td className="px-3 py-2 min-w-[140px]">
                                    <CustomSelect
                                      value={g.key}
                                      onChange={(e) =>
                                        moveCircuitToGroup(
                                          c.circuit_id,
                                          g.key,
                                          e?.target?.value ?? ""
                                        )
                                      }
                                      options={groups.map((opt, oi) => ({
                                        value: opt.key,
                                        label: `Group ${oi + 1}${
                                          opt.tracking_number
                                            ? ` (${opt.tracking_number})`
                                            : ""
                                        }`,
                                      }))}
                                      className="!min-h-[32px] py-1 text-[12px] bg-[var(--sunken,#eeece7)]"
                                      aria-label="Move to group"
                                    />
                                  </td>
                                ) : null}
                                <td className="px-2 py-2">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      removeCircuit(g.key, c.circuit_id)
                                    }
                                    className="rounded p-1 text-[var(--muted,#6b6a66)] hover:bg-[var(--sunken,#eeece7)]"
                                    aria-label={`Remove ${c.circuit_id}`}
                                    disabled={allCircuits.length <= 1}
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                        {(g.circuits || []).length > 0 ? (
                          <tfoot>
                            <tr className="border-t border-[var(--line,#e4e2dc)] bg-[var(--sunken,#eeece7)] text-[12px] font-medium">
                              <td className="px-3 py-2" colSpan={4}>
                                {(g.circuits || []).length} circuit
                                {(g.circuits || []).length === 1 ? "" : "s"}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums">
                                {formatMoney(
                                  (g.circuits || []).reduce(
                                    (s, c) => s + (Number(c.mrc) || 0),
                                    0
                                  )
                                )}
                              </td>
                              <td
                                colSpan={groups.length > 1 ? 2 : 1}
                              />
                            </tr>
                          </tfoot>
                        ) : null}
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Details */}
            <section>
              <h3 className="mb-3 text-[14px] font-semibold text-[var(--ink,#1c1c1a)]">
                Details
              </h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
                    Requested by
                  </span>
                  <input
                    type="text"
                    value={requestedBy}
                    onChange={(e) => setRequestedBy(e.target.value)}
                    className={fieldClass}
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
                    Reason
                  </span>
                  <CustomSelect
                    value={reason}
                    onChange={(e) => setReason(e?.target?.value ?? "")}
                    options={[
                      { value: "", label: "Select a reason" },
                      ...DISCONNECT_REASONS.map((r) => ({
                        value: r,
                        label: r,
                      })),
                    ]}
                    className={fieldClass}
                    placeholder="Select a reason"
                  />
                </label>
                <label className="block sm:col-span-2">
                  <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
                    Notes
                  </span>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    placeholder="Anything the next person should know"
                    className={fieldClass}
                  />
                </label>
              </div>
            </section>

            {/* Evidence */}
            <section>
              <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="text-[14px] font-semibold text-[var(--ink,#1c1c1a)]">
                  Evidence
                </h3>
                <p className="text-[12px] text-[var(--muted,#6b6a66)]">
                  The email, PDF or screenshot showing the disconnect was
                  requested or confirmed. Same files apply to all circuits on
                  this request.
                </p>
              </div>
              <label
                className="flex cursor-pointer items-center gap-3 rounded-[10px] border border-dashed border-[var(--accent,#a51c1c)] bg-[var(--accent-soft,#fbe4e4)]/40 px-4 py-4 hover:bg-[var(--accent-soft,#fbe4e4)]/70"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  onFilesPicked(e.dataTransfer.files);
                }}
              >
                <Paperclip className="h-5 w-5 shrink-0 text-[var(--accent,#a51c1c)]" />
                <div className="min-w-0">
                  <p className="text-[13px] font-medium text-[var(--ink,#1c1c1a)]">
                    Drop files here or click to upload.
                  </p>
                  <p className="text-[12px] text-[var(--muted,#6b6a66)]">
                    Accepts .eml, .msg, PDF, images, Word and text — and any
                    other file. Several at once are fine.
                  </p>
                </div>
                <input
                  type="file"
                  className="hidden"
                  multiple
                  accept={ACCEPTED_EXTENSIONS}
                  onChange={(e) => {
                    onFilesPicked(e.target.files);
                    e.target.value = "";
                  }}
                />
              </label>
              {files.length === 0 ? (
                <p className="mt-2 text-[12px] text-[var(--muted,#6b6a66)]">
                  No evidence on file yet.
                </p>
              ) : (
                <ul className="mt-3 space-y-2">
                  {files.map((file, index) => (
                    <li
                      key={`${file.name}-${index}`}
                      className="flex items-center gap-3 rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white px-3 py-2"
                    >
                      {fileKindLabel(file) === "Image" ? (
                        <ImageIcon className="h-5 w-5 text-[var(--muted,#6b6a66)]" />
                      ) : (
                        <FileText className="h-5 w-5 text-[var(--muted,#6b6a66)]" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[13px] font-medium">
                          {file.name}
                        </p>
                        <p className="text-[11px] text-[var(--muted,#6b6a66)]">
                          {fileKindLabel(file)} · {formatBytes(file.size)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const url = URL.createObjectURL(file);
                          window.open(url, "_blank", "noopener,noreferrer");
                        }}
                        className="rounded p-1 text-[var(--muted,#6b6a66)] hover:bg-[var(--sunken,#eeece7)]"
                        aria-label="Open"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="rounded p-1 text-[var(--muted,#6b6a66)] hover:bg-[var(--sunken,#eeece7)]"
                        aria-label="Remove file"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>

          </div>

          <div className="flex flex-shrink-0 items-center justify-between gap-3 border-t border-[var(--line,#e4e2dc)] px-6 py-3">
            <p className="text-[12px] text-[var(--muted,#6b6a66)]">
              Saved to each circuit&apos;s history and the Disconnects report.
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white px-4 py-2 text-[13px] font-medium text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || allCircuits.length === 0}
                className="inline-flex items-center justify-center rounded-md bg-[var(--accent,#a51c1c)] px-4 py-2 text-[13px] font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submitting ? "Submitting…" : "Submit request"}
              </button>
            </div>
          </div>
        </form>

        {showAddCircuits ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/30 p-6">
            <div className="flex max-h-full w-full max-w-lg flex-col overflow-hidden rounded-[12px] border border-[var(--line,#e4e2dc)] bg-white shadow-lg">
              <div className="flex items-center justify-between border-b border-[var(--line,#e4e2dc)] px-4 py-3">
                <div>
                  <h4 className="text-[14px] font-semibold">
                    Add {provider} circuits
                  </h4>
                  <p className="text-[12px] text-[var(--muted,#6b6a66)]">
                    Other providers are excluded.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowAddCircuits(false)}
                  className="rounded p-1 hover:bg-[var(--sunken,#eeece7)]"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="px-4 py-2">
                <input
                  type="search"
                  value={pickerSearch}
                  onChange={(e) => setPickerSearch(e.target.value)}
                  placeholder="Search circuit, account, or address"
                  className={fieldClass}
                />
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
                {pickerLoading ? (
                  <p className="px-2 py-4 text-[13px] text-[var(--muted,#6b6a66)]">
                    Loading…
                  </p>
                ) : pickerRows.length === 0 ? (
                  <p className="px-2 py-4 text-[13px] text-[var(--muted,#6b6a66)]">
                    No matching circuits.
                  </p>
                ) : (
                  <ul className="space-y-1">
                    {pickerRows.map((r) => {
                      const checked = selectedPickerIds.has(r.inventory_id);
                      return (
                        <li key={r.inventory_id}>
                          <label className="flex cursor-pointer items-start gap-2 rounded-md px-2 py-2 hover:bg-[var(--sunken,#eeece7)]">
                            <input
                              type="checkbox"
                              className="mt-1"
                              checked={checked}
                              onChange={() => {
                                setSelectedPickerIds((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(r.inventory_id)) {
                                    next.delete(r.inventory_id);
                                  } else {
                                    next.add(r.inventory_id);
                                  }
                                  return next;
                                });
                              }}
                            />
                            <span className="min-w-0">
                              <span className="block text-[13px] font-medium">
                                {r.circuit_id}
                              </span>
                              <span className="block text-[12px] text-[var(--muted,#6b6a66)]">
                                {r.address || "—"} · {formatMoney(r.mrc)}
                              </span>
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <div className="flex justify-end gap-2 border-t border-[var(--line,#e4e2dc)] px-4 py-3">
                <button
                  type="button"
                  onClick={() => setShowAddCircuits(false)}
                  className="rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white px-3 py-1.5 text-[13px]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={selectedPickerIds.size === 0}
                  onClick={addSelectedCircuits}
                  className="rounded-md bg-[var(--accent,#a51c1c)] px-3 py-1.5 text-[13px] font-medium text-white disabled:opacity-50"
                >
                  Add {selectedPickerIds.size || ""}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default DisconnectRequestModal;
